import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "./components/site/Layout";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LangProvider } from "./lib/i18n";

import Home from "./pages/Home";
import About from "./pages/About";
import Expertise from "./pages/Expertise";
import ExpertiseDetail from "./pages/ExpertiseDetail";
import ContentHub from "./pages/ContentHub";
import HubArticle from "./pages/HubArticle";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import Appointment from "./pages/Appointment";
import Testimonials from "./pages/Testimonials";
import Media from "./pages/Media";
import Videos from "./pages/Videos";
import Publications from "./pages/Publications";
import Press from "./pages/Press";
import Insurance from "./pages/Insurance";
import Location from "./pages/Location";
import Guides from "./pages/Guides";
import CityService from "./pages/CityService";
import DrManishaMishra from "./pages/DrManishaMishra";
import BookSquintSimplified from "./pages/BookSquintSimplified";
import Login from "./pages/Login";
import Admin from "./pages/Admin";
import NotFound from "./pages/NotFound";
import BestOfVaranasi from "./pages/BestOfVaranasi";
import CataractCost from "./pages/CataractCost";
import CataractCalculator from "./pages/CataractCalculator";
import EmergencyEyeCare from "./pages/EmergencyEyeCare";
import Review from "./pages/Review";
import Sitemap from "./pages/Sitemap";
import EyeCases from "./pages/EyeCases";
import EyeCaseDetail from "./pages/EyeCaseDetail";
import IOLSelector from "./pages/IOLSelector";
import RoleHub from "./pages/RoleHub";
import InternationalPatients from "./pages/InternationalPatients";
import SymptomChecker from "./pages/SymptomChecker";
import SymptomPage from "./pages/SymptomPage";
import ScrollToTop from "./components/site/ScrollToTop";
import AutoTranslator from "./components/site/AutoTranslator";
import { LANDING_PAGES } from "./data/landingPages";

import "./App.css";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
};

const SitePage = ({ children }) => <Layout>{children}</Layout>;

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <LangProvider>
          <BrowserRouter>
          <ScrollToTop />
          <AutoTranslator />
          <Toaster position="top-center" richColors closeButton />
          <Routes>
            <Route path="/" element={<SitePage><Home /></SitePage>} />
            <Route path="/about" element={<SitePage><About /></SitePage>} />
            <Route path="/expertise" element={<SitePage><Expertise /></SitePage>} />
            <Route path="/expertise/:slug" element={<SitePage><ExpertiseDetail /></SitePage>} />
            <Route path="/content-hub" element={<SitePage><ContentHub /></SitePage>} />
            <Route path="/content-hub/:slug" element={<SitePage><HubArticle /></SitePage>} />
            <Route path="/blog" element={<SitePage><Blog /></SitePage>} />
            <Route path="/blog/:slug" element={<SitePage><BlogPost /></SitePage>} />
            <Route path="/faq" element={<SitePage><FAQ /></SitePage>} />
            <Route path="/testimonials" element={<SitePage><Testimonials /></SitePage>} />
            <Route path="/media" element={<SitePage><Media /></SitePage>} />
            <Route path="/videos" element={<SitePage><Videos /></SitePage>} />
            <Route path="/publications" element={<SitePage><Publications /></SitePage>} />
            <Route path="/press" element={<SitePage><Press /></SitePage>} />
            <Route path="/press-kit" element={<SitePage><Press /></SitePage>} />
            <Route path="/appointment" element={<SitePage><Appointment /></SitePage>} />
            <Route path="/insurance" element={<SitePage><Insurance /></SitePage>} />
            <Route path="/locations/:city" element={<SitePage><Location /></SitePage>} />
            <Route path="/varanasi/:city" element={<SitePage><Location /></SitePage>} />
            <Route path="/guides" element={<SitePage><Guides /></SitePage>} />
            <Route path="/contact" element={<SitePage><Contact /></SitePage>} />
            <Route path="/team/dr-manisha-mishra" element={<SitePage><DrManishaMishra /></SitePage>} />
            <Route path="/book/squint-simplified" element={<SitePage><BookSquintSimplified /></SitePage>} />
            <Route path="/best-eye-doctor-varanasi" element={<SitePage><BestOfVaranasi /></SitePage>} />
            <Route path="/cataract-surgery-cost-in-varanasi" element={<SitePage><CataractCost /></SitePage>} />
            <Route path="/tools/cataract-cost-calculator" element={<SitePage><CataractCalculator /></SitePage>} />
            <Route path="/emergency-eye-care-varanasi" element={<SitePage><EmergencyEyeCare /></SitePage>} />
            <Route path="/review" element={<SitePage><Review /></SitePage>} />
            <Route path="/sitemap" element={<SitePage><Sitemap /></SitePage>} />
            <Route path="/sitemap.html" element={<SitePage><Sitemap /></SitePage>} />
            <Route path="/eye-cases" element={<SitePage><EyeCases /></SitePage>} />
            <Route path="/eye-cases/:slug" element={<SitePage><EyeCaseDetail /></SitePage>} />
            <Route path="/iol-selector" element={<SitePage><IOLSelector /></SitePage>} />
            <Route path="/cataract-lens-selection-in-varanasi" element={<SitePage><IOLSelector /></SitePage>} />
            {/* Role-based specialist hubs (Phase A) + high-intent access hubs (Phase F) */}
            <Route path="/best-cataract-surgeon-in-varanasi" element={<SitePage><RoleHub /></SitePage>} />
            <Route path="/best-retina-specialist-in-varanasi" element={<SitePage><RoleHub /></SitePage>} />
            <Route path="/best-glaucoma-specialist-in-varanasi" element={<SitePage><RoleHub /></SitePage>} />
            <Route path="/best-pediatric-eye-specialist-in-varanasi" element={<SitePage><RoleHub /></SitePage>} />
            <Route path="/best-cornea-specialist-in-varanasi" element={<SitePage><RoleHub /></SitePage>} />
            <Route path="/best-squint-surgeon-in-varanasi" element={<SitePage><RoleHub /></SitePage>} />
            <Route path="/best-oculoplasty-surgeon-in-varanasi" element={<SitePage><RoleHub /></SitePage>} />
            <Route path="/insurance/ayushman-bharat-eye-hospital-varanasi" element={<SitePage><RoleHub /></SitePage>} />
            <Route path="/cashless-eye-surgery-varanasi" element={<SitePage><RoleHub /></SitePage>} />
            <Route path="/emergency-eye-doctor-varanasi" element={<SitePage><RoleHub /></SitePage>} />
            <Route path="/second-opinion-eye-surgery-varanasi" element={<SitePage><RoleHub /></SitePage>} />
            <Route path="/eye-doctor-for-bihar-patients" element={<SitePage><RoleHub /></SitePage>} />
            <Route path="/international-patients" element={<SitePage><InternationalPatients /></SitePage>} />
            <Route path="/symptom-checker" element={<SitePage><SymptomChecker /></SitePage>} />
            <Route path="/symptoms/:slug" element={<SitePage><SymptomPage /></SitePage>} />

            {/* Keyword-rich city+service landing pages (SEO). Each slug is
                explicit so routes don't collide with anything else. */}
            {LANDING_PAGES.map((p) => (
              <Route
                key={p.slug}
                path={`/${p.slug}`}
                element={<SitePage><CityService /></SitePage>}
              />
            ))}

            <Route path="/admin/login" element={<Login />} />
            <Route path="/admin" element={<Protected><Admin /></Protected>} />
            <Route path="*" element={<SitePage><NotFound /></SitePage>} />
          </Routes>
        </BrowserRouter>
        </LangProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
